// Mock data for BlueMP website

export const companyInfo = {
  name: "Blue MP",
  tagline: "Blending AI with Integrity",
  extendedTagline: "Powered by AI, guided by integrity — delivering trust, innovation, and reliability.",
  description: "South African-based AI consulting partner focusing on responsible, high-impact artificial intelligence solutions.",
  vision: "To make AI practical, ethical, and accessible, empowering organizations with smarter, data-driven decisions and human accountability.",
  pillars: ["Trust", "Innovation", "Integrity", "Reliability"],
  contact: {
    phone: "+27 84 512 3122",
    email: "info@bluemp.biz",
    website: "www.bluemp.biz",
    location: "Cape Town, South Africa"
  }
};

export const services = [
  {
    id: "ai-consulting",
    title: "AI Consulting and Strategy",
    subtitle: "From Insight to Implementation",
    description: "Turning business challenges into executable AI roadmaps with clear KPIs and measurable outcomes.",
    capabilities: [
      "Consulting & Discovery",
      "Business Analysis",
      "Proposal & Roadmap",
      "Implementation Support"
    ],
    benefits: [
      "Clear AI vision and strategy",
      "Inefficiency discovery and optimization",
      "Low-risk, high-impact execution",
      "Seamless transition to implementation"
    ]
  },
  {
    id: "contactability",
    title: "Contactability AI Solution", 
    subtitle: "Reach the Right People, the Right Way",
    description: "Verified, enriched customer data for improved reach and compliance through intelligent data processing.",
    capabilities: [
      "Data verification and enrichment", 
      "Contact quality scoring",
      "Channel and response prediction",
      "CRM integration and automation"
    ],
    benefits: [
      "Verified and accurate customer data",
      "Higher engagement rates",
      "Reduced marketing waste",
      "Improved compliance tracking"
    ]
  },
  {
    id: "broker-analytics",
    title: "Broker Firm AI Analytics",
    subtitle: "Empowering Broker Firms with a 360° View", 
    description: "Unified client, portfolio, and AUM intelligence consolidating insurer and investment data for smarter decisions.",
    capabilities: [
      "Data consolidation and unification",
      "Portfolio and AUM analysis", 
      "Adviser performance tracking",
      "Real-time dashboards and reporting"
    ],
    benefits: [
      "Unified visibility across all data sources",
      "Data-driven decision making",
      "Increased adviser efficiency", 
      "Enhanced client relationship management"
    ]
  },
  {
    id: "campaign-solutions", 
    title: "AI Campaign Solutions",
    subtitle: "Smarter Campaigns. Stronger Connections.",
    description: "Precision targeting and intelligent engagement moving from broad marketing to personalized customer connections.",
    capabilities: [
      "AI-driven customer segmentation",
      "Predictive modeling and scoring",
      "Automated channel optimization", 
      "Real-time performance dashboards"
    ],
    benefits: [
      "Higher conversion rates",
      "Personalized customer communication",
      "Improved marketing ROI",
      "Reduced acquisition costs"
    ]
  },
  {
    id: "unclaimed-funds",
    title: "Unclaimed / Unpaid AI Solution",
    subtitle: "Intelligent Tracing with Integrity", 
    description: "Responsible tracing powered by intelligent data matching for ethical and efficient beneficiary identification.",
    capabilities: [
      "AI-driven data matching and enrichment",
      "Beneficiary tracing and validation",
      "Compliance audit trails",
      "Secure data processing"
    ],
    benefits: [
      "Higher identification accuracy",
      "Reduced manual processing work", 
      "Transparent compliance reporting",
      "Ethical data handling practices"
    ]
  }
];

export const learningPrograms = [
  {
    title: "AI Awareness & Foundation",
    description: "Building fundamental understanding of AI capabilities, limitations, and practical applications across business functions.",
    outcomes: ["Clear AI comprehension", "Practical application knowledge", "Risk awareness and mitigation"]
  },
  {
    title: "Applied AI for Professionals", 
    description: "Hands-on training for professionals to integrate AI tools and processes into their daily workflows effectively.",
    outcomes: ["Tool proficiency", "Workflow integration", "Productivity enhancement"]
  },
  {
    title: "AI Strategy for Leaders",
    description: "Strategic guidance for leadership teams to make informed AI investment and implementation decisions.", 
    outcomes: ["Strategic clarity", "Investment prioritization", "Change management readiness"]
  }
];

export const experienceHighlights = [
  {
    metric: "10M+",
    description: "Customers engaged through our AI solutions"
  },
  {
    metric: "Multi-Million",
    description: "Rand revenue contributions supported"
  },
  {
    metric: "100%", 
    description: "Ethical compliance in tracing solutions"
  },
  {
    metric: "360°",
    description: "Unified analytics for broker intelligence"
  }
];

export const sectors = [
  "Financial Services",
  "Insurance", 
  "Communications",
  "Banking",
  "Investment Management"
];

export const insightArticles = [
  {
    id: 1,
    title: "The Ethics of AI: Building Trust in Automated Decision Making",
    excerpt: "Exploring how organizations can implement AI systems that maintain human accountability and ethical standards.",
    category: "AI Ethics",
    readTime: "8 min read",
    date: "2025-01-15"
  },
  {
    id: 2, 
    title: "Practical AI Implementation: Lessons from South African Financial Services",
    excerpt: "Real-world insights from successful AI deployments in the financial sector, including challenges and solutions.",
    category: "Case Studies", 
    readTime: "12 min read",
    date: "2025-01-10"
  },
  {
    id: 3,
    title: "ROI Measurement in AI Projects: Beyond the Hype",
    excerpt: "A framework for measuring tangible business value and return on investment in AI initiatives.",
    category: "Business Strategy",
    readTime: "10 min read", 
    date: "2025-01-05"
  },
  {
    id: 4,
    title: "Data Quality: The Foundation of Successful AI Solutions", 
    excerpt: "Understanding why data quality is critical for AI success and practical steps to improve it.",
    category: "Data Management",
    readTime: "6 min read",
    date: "2024-12-28"
  }
];

export const faqs = [
  {
    question: "How does Blue MP ensure AI implementations are ethical and compliant?",
    answer: "We follow strict ethical guidelines and compliance frameworks, ensuring all AI solutions maintain human accountability, transparency, and respect for privacy. Our approach includes audit trails, explainable AI methods, and continuous monitoring."
  },
  {
    question: "What makes Blue MP different from other AI consulting firms?",
    answer: "Our unique combination of deep business experience and technical expertise, coupled with our unwavering commitment to integrity, sets us apart. We focus on practical, measurable outcomes rather than theoretical implementations."
  },
  {
    question: "How long does a typical AI project take to implement?",
    answer: "Project timelines vary based on complexity and scope, typically ranging from 3-12 months. We follow a phased approach that delivers value incrementally, allowing for early wins and continuous improvement."
  },
  {
    question: "Do you provide ongoing support after AI solution implementation?",
    answer: "Yes, we offer comprehensive post-implementation support including monitoring, optimization, training, and maintenance to ensure your AI solutions continue delivering value over time."
  }
];

export const testimonials = [
  {
    name: "Sarah Johnson",
    position: "Head of Operations",
    company: "Financial Services Firm",
    quote: "Blue MP transformed our customer engagement process. Their contactability solution improved our reach by 40% while maintaining full compliance.",
    rating: 5
  },
  {
    name: "Michael Chen", 
    position: "CEO",
    company: "Insurance Brokerage",
    quote: "The unified analytics platform gave us insights we never had before. Decision-making is now data-driven and our advisers are more effective.",
    rating: 5
  },
  {
    name: "Linda van der Merwe",
    position: "Marketing Director", 
    company: "Investment Management",
    quote: "Their AI campaign solutions revolutionized our marketing approach. ROI increased by 60% with more personalized customer experiences.",
    rating: 5
  }
];